// src/pages/deals/InvoiceInsights.tsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import ScrollToTop from "../../../core/common/ScrollToTop";
import { fetchAllInvoices, Invoice } from "../../../api/invoiceproposal";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store"; // adjust if path differs
import moment from "moment";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TimeScale,
  ChartOptions,
} from "chart.js";
import { Pie, Doughnut, Bar, Line, getElementAtEvent } from "react-chartjs-2";
import "chartjs-adapter-moment";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import Tip from "../../../core/common/tooltip";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TimeScale
);

type PeriodKey =
  | "overall"
  | "last_7_days"
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "this_year"
  | "last_year";

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "overall", label: "Overall" },
  { key: "last_7_days", label: "Last 7 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_3_months", label: "Last 3 months" },
  { key: "last_6_months", label: "Last 6 months" },
  { key: "this_year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
];

const getPeriodRange = (key: PeriodKey) => {
  if (key === "overall") {
    return {
      start: moment("2025-01-01"),
      end: moment().add(100, "years"),
    };
  }

  let start = moment().startOf("day");
  let end = moment().endOf("day");

  switch (key) {
    case "last_7_days":
      start = moment().subtract(6, "days").startOf("day");
      break;

    case "this_month":
      start = moment().startOf("month");
      break;

    case "last_month":
      start = moment().subtract(1, "month").startOf("month");
      end = moment(start).endOf("month");
      break;

    case "last_3_months":
      start = moment().subtract(3, "months").startOf("day");
      break;

    case "last_6_months":
      start = moment().subtract(6, "months").startOf("day");
      break;

    case "this_year":
      start = moment().startOf("year");
      break;

    case "last_year":
      start = moment().subtract(1, "year").startOf("year");
      end = moment(start).endOf("year");
      break;
  }

  return { start, end };
};

export default function InvoiceInsights() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const route = all_routes;

  // Global (overall) period filter - default this_month
  const [globalPeriod, setGlobalPeriod] = useState<PeriodKey>("this_month");

  // Section specific period overrides (if null -> use globalPeriod)
  const [sectionPeriods, setSectionPeriods] = useState<
    Partial<Record<string, PeriodKey>>
  >({});

  // other filters
  const [showCurrency, setShowCurrency] = useState<string | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const userId = useSelector((s: RootState) => s.auth?.user_id);

  // Chart refs (for safe access)
  const ownerBarRef = useRef<any>(null);

  // load invoices once
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllInvoices();
      setInvoices(data || []);
    } catch (err) {
      console.error("Failed to fetch invoices for insights", err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // helper to get effective period for a section
  const getEffectivePeriod = (sectionKey: string): PeriodKey => {
    return (sectionPeriods[sectionKey] as PeriodKey) ?? globalPeriod;
  };

  // filter invoices by a chosen period & other filters (ownerFilter / showCurrency)
  const filterInvoicesForPeriod = (periodKey: PeriodKey) => {
    const { start, end } = getPeriodRange(periodKey);
    return invoices.filter((inv) => {
      const dateStr = inv.invoice_date || inv.created_at || inv.validation_date;
      if (!dateStr) return false;
      const invMoment = moment(dateStr);
      if (!invMoment.isValid()) return false;
      if (!invMoment.isBetween(start, end, undefined, "[]")) return false;

      if (ownerFilter) {
        const ownerName = inv.deal?.contact?.users?.name || "";
        if (ownerName !== ownerFilter) return false;
      }

      if (showCurrency) {
        const currency = inv.deal?.currency || "";
        if (currency !== showCurrency) return false;
      }

      // 🔍 NEW: Status filter (Paid / Not Paid / Partial)
      if (statusFilter) {
        const st = (inv.status || "").toLowerCase();
        if (st !== statusFilter) return false;
      }

      return true;
    });
  };

  // SECTION: Status Summary (uses effective period for 'status')
  const statusPeriod = getEffectivePeriod("status");
  const statusInvoices = useMemo(
    () => filterInvoicesForPeriod(statusPeriod),
    [invoices, statusPeriod, ownerFilter, showCurrency]
  );
  const statusSummary = useMemo(() => {
    const counts: Record<string, { count: number; revenue: number }> = {};

    statusInvoices.forEach((inv) => {
      const status = (inv.status || "unknown").toLowerCase();

      // If a statusFilter exists, ignore other statuses
      if (statusFilter && status !== statusFilter) return;

      const revenue = Number(
        inv.invoice_total_amount ?? inv.invoice_amount ?? 0
      );

      if (!counts[status]) counts[status] = { count: 0, revenue: 0 };
      counts[status].count += 1;
      counts[status].revenue += revenue;
    });

    return counts;
  }, [statusInvoices, statusFilter]);

  const buildRecentRevenueSeries = (invoices: Invoice[], days = 7) => {
    const arr: number[] = [];
    for (let d = days - 1; d >= 0; d--) {
      const day = moment().subtract(d, "days").startOf("day");
      const dayEnd = moment(day).endOf("day");
      const total = invoices
        .filter((inv) => {
          const dateStr =
            inv.invoice_date || inv.created_at || inv.validation_date;
          if (!dateStr) return false;
          const m = moment(dateStr);
          return m.isBetween(day, dayEnd, undefined, "[]");
        })
        .reduce(
          (s, i) => s + Number(i.invoice_total_amount ?? i.invoice_amount ?? 0),
          0
        );
      arr.push(total);
    }
    return arr;
  };

  // small sparkline chart options shared by cards
  const sparkOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      point: { radius: 0 },
      line: { tension: 0.3 },
    },
    scales: {
      x: { display: false },
      y: { display: false },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  };

  const toggleStatus = (s: string) => {
    setStatusFilter((prev) => (prev === s ? null : s));
  };

  // SECTION: Owner Activity (uses its own effective period)
  const ownerPeriod = getEffectivePeriod("owner");
  const ownerInvoices = useMemo(
    () => filterInvoicesForPeriod(ownerPeriod),
    [invoices, ownerPeriod, showCurrency]
  );
  const ownerActivity = useMemo(() => {
    const map: Record<
      string,
      {
        total: number;
        paid: number;
        notpaid: number;
        partial: number;
        revenue: number;
      }
    > = {};
    ownerInvoices.forEach((inv) => {
      const owner = inv.deal?.contact?.users?.name ?? "Unassigned";
      const st = (inv.status || "unknown").toLowerCase();
      const rev = Number(inv.invoice_total_amount ?? inv.invoice_amount ?? 0);
      if (!map[owner])
        map[owner] = { total: 0, paid: 0, notpaid: 0, partial: 0, revenue: 0 };
      map[owner].total += 1;
      map[owner].revenue += rev;
      if (st === "paid") map[owner].paid += 1;
      else if (st === "notpaid") map[owner].notpaid += 1;
      else if (st === "partialpaid" || st === "partial")
        map[owner].partial += 1;
    });
    return map;
  }, [ownerInvoices]);

  // SECTION: Product Activity (uses product-specific effective period)
  const productPeriod = getEffectivePeriod("product");
  const productInvoices = useMemo(
    () => filterInvoicesForPeriod(productPeriod),
    [invoices, productPeriod, ownerFilter, showCurrency]
  );
  const productActivity = useMemo(() => {
    const map: Record<
      string,
      { count: number; revenue: number; paid: number; notpaid: number }
    > = {};
    productInvoices.forEach((inv) => {
      const product = inv.deal?.product?.product_name ?? "Unknown Product";
      const st = (inv.status || "unknown").toLowerCase();
      const rev = Number(
        inv.invoice_total_amount ??
          inv.invoice_amount ??
          inv.deal?.deal_value ??
          0
      );
      if (!map[product])
        map[product] = { count: 0, revenue: 0, paid: 0, notpaid: 0 };
      map[product].count += 1;
      map[product].revenue += rev;
      if (st === "paid") map[product].paid += 1;
      else map[product].notpaid += 1;
    });
    return map;
  }, [productInvoices]);

  // Upcoming dates don't use section override - they always use overall invoices (you can change if needed)
  const upcomingValidation = useMemo(() => {
    const now = moment();
    const next30 = moment().add(30, "days").endOf("day");
    return invoices
      .filter((inv) => {
        if (!inv.validation_date) return false;
        const m = moment(inv.validation_date);
        return m.isBetween(now, next30, undefined, "[]");
      })
      .sort(
        (a, b) =>
          moment(a.validation_date).valueOf() -
          moment(b.validation_date).valueOf()
      );
  }, [invoices]);

  const upcomingInvoiceDates = useMemo(() => {
    const now = moment();
    const next30 = moment().add(30, "days").endOf("day");
    return invoices
      .filter((inv) => {
        if (!inv.invoice_date) return false;
        const m = moment(inv.invoice_date);
        return m.isBetween(now, next30, undefined, "[]");
      })
      .sort(
        (a, b) =>
          moment(a.invoice_date).valueOf() - moment(b.invoice_date).valueOf()
      );
  }, [invoices]);

  // Timeseries - uses its own effective period
  const timeseriesPeriod = getEffectivePeriod("timeseries");
  const timeseries = useMemo(() => {
    const { start, end } = getPeriodRange(timeseriesPeriod);
    const daysSpan = end.diff(start, "days") + 1;
    const groupByMonth = daysSpan > 90;
    const buckets: Record<string, number> = {};
    const labels: string[] = [];

    if (groupByMonth) {
      const cur = start.clone().startOf("month");
      while (cur.isSameOrBefore(end)) {
        const key = cur.format("YYYY-MM");
        labels.push(key);
        buckets[key] = 0;
        cur.add(1, "month");
      }
    } else {
      const cur = start.clone();
      while (cur.isSameOrBefore(end)) {
        const key = cur.format("YYYY-MM-DD");
        labels.push(key);
        buckets[key] = 0;
        cur.add(1, "day");
      }
    }

    // iterate only invoices filtered by timeseries period + global owner/currency filters
    const filtered = filterInvoicesForPeriod(timeseriesPeriod);
    filtered.forEach((inv) => {
      const dateStr = inv.invoice_date || inv.created_at || inv.validation_date;
      const m = moment(dateStr);
      if (!m.isValid()) return;
      const key = groupByMonth ? m.format("YYYY-MM") : m.format("YYYY-MM-DD");
      const rev = Number(
        inv.invoice_total_amount ??
          inv.invoice_amount ??
          inv.deal?.deal_value ??
          0
      );
      buckets[key] = (buckets[key] || 0) + rev;
    });

    return {
      labels,
      data: labels.map((l) => buckets[l] || 0),
      groupByMonth,
    };
  }, [invoices, timeseriesPeriod, ownerFilter, showCurrency]);

  // Chart data builders
  const statusPieData = useMemo(() => {
    const keys = Object.keys(statusSummary);
    const counts = keys.map((k) => statusSummary[k].count);
    // simple palette — you can expand
    const palette = ["#0d6efd", "#198754", "#dc3545", "#ffc107", "#6c757d"];
    return {
      labels: keys.map((k) => k.toUpperCase()),
      datasets: [
        {
          data: counts,
          backgroundColor: keys.map((_, i) => palette[i % palette.length]),
        },
      ],
    };
  }, [statusSummary]);

  const ownerBarData = useMemo(() => {
    const owners = Object.keys(ownerActivity);
    const paidCounts = owners.map((o) => ownerActivity[o].paid);
    const notPaidCounts = owners.map(
      (o) => ownerActivity[o].notpaid + ownerActivity[o].partial
    );
    return {
      labels: owners,
      datasets: [
        {
          label: "Paid",
          data: paidCounts,
          backgroundColor: "#198754",
          stack: "stack1",
        },
        {
          label: "Not Paid/Partial",
          data: notPaidCounts,
          backgroundColor: "#dc3545",
          stack: "stack1",
        },
      ],
    };
  }, [ownerActivity]);

  const productDoughnutData = useMemo(() => {
    const prods = Object.keys(productActivity);
    const revs = prods.map((p) => productActivity[p].revenue);
    const palette = [
      "#0d6efd",
      "#6610f2",
      "#6f42c1",
      "#d63384",
      "#fd7e14",
      "#ffc107",
      "#198754",
      "#20c997",
      "#0dcaf0",
      "#6c757d",
    ];
    return {
      labels: prods,
      datasets: [
        {
          data: revs,
          backgroundColor: prods.map((_, i) => palette[i % palette.length]),
        },
      ],
    };
  }, [productActivity]);

  const timeseriesData = useMemo(() => {
    return {
      labels: timeseries.labels,
      datasets: [
        {
          label: "Revenue",
          data: timeseries.data,
          fill: false,
          tension: 0.2,
          borderColor: "#0d6efd",
          backgroundColor: "#0d6efd",
        },
      ],
    };
  }, [timeseries]);

  // safe click handler for owner bar chart
  const onOwnerBarClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!ownerBarRef.current) return;

    const elements = getElementAtEvent(ownerBarRef.current, event);

    if (!elements || elements.length === 0) return;

    const chart = ownerBarRef.current;
    const index = elements[0].index;

    const owner = chart.data.labels[index];
    if (!owner) return;

    setOwnerFilter((prev) => (prev === owner ? null : owner));
  };

  // helper UI functions
  const setSectionPeriod = (section: string, p: PeriodKey | "") => {
    setSectionPeriods((s) => {
      const copy = { ...s };
      if (!p) delete copy[section];
      else copy[section] = p;
      return copy;
    });
  };

  // computed totals for header quick cards — use overall/globalPeriod
  const overallInvoices = useMemo(
    () => filterInvoicesForPeriod(globalPeriod),
    [invoices, globalPeriod, ownerFilter, showCurrency]
  );
  const overallRevenue = useMemo(
    () =>
      overallInvoices.reduce(
        (s, i) => s + Number(i.invoice_total_amount ?? i.invoice_amount ?? 0),
        0
      ),
    [overallInvoices]
  );

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="card mb-3">
            <div className="card-header py-3">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                {/* LEFT: Title stacked with subtitle */}
                <div className="">
                  <h3 className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-bank2 text-primary"></i>
                    Invoice Proposal Insights
                    <Tip
                      text="This dashboard shows invoice revenue, status distribution, owner performance, product-wise revenue, and upcoming invoice/validation dates."
                      placement="right"
                    >
                      <i
                        className="fa fa-info-circle text-primary"
                        style={{ cursor: "pointer", fontSize: 16 }}
                      />
                    </Tip>
                  </h3>

                  <p className="text-muted mb-0 small">
                    Real-time metrics and performance overview of all invoice
                    proposals.
                  </p>
                   {/* Breadcrumb */}
                  <ol className="breadcrumb mb-0 small">
                    <li className="breadcrumb-item">
                      <Link to={route.InvoiceList} 
                       style={{
                        color: "#9595b5",
                        textDecoration: "none",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#E41F07")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#9595b5")
                      }
                      >
                        Invoice
                      </Link>
                    </li>
                    <li className="breadcrumb-item active">Invoice Insights</li>
                  </ol>
                </div>

                {/* RIGHT: Filters */}
                <div className="d-flex gap-2 align-items-center mt-3 mt-md-0">
                  <select
                    className="form-select form-select-sm"
                    value={globalPeriod}
                    onChange={(e) =>
                      setGlobalPeriod(e.target.value as PeriodKey)
                    }
                  >
                    {PERIODS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>

                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => {
                      setOwnerFilter(null);
                      setShowCurrency(null);
                      setSectionPeriods({});
                      setGlobalPeriod("this_month");
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body">
              {/* Quick KPI cards using overall (global) */}
              <div className="row mb-4 g-3 ">
                {/* Card 1: Invoices overall */}
                <div className="col-md-3">
                  <div className="card h-100 border-primary shadow-sm dashboard-card">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <small className="text-muted">
                            Invoices (overall)
                          </small>
                          <div className="d-flex align-items-center gap-2">
                            <h3 className="mb-0">{overallInvoices.length}</h3>
                            <span className="badge bg-primary align-self-start">
                              {" "}
                              {
                                PERIODS.find((p) => p.key === globalPeriod)
                                  ?.label
                              }{" "}
                            </span>
                          </div>
                        </div>
                        <div>
                          <i
                            className="bi bi-receipt-cutoff fs-3 text-primary"
                            aria-hidden
                          />
                        </div>
                      </div>

                      <div style={{ height: 40 }}>
                        <Line
                          data={{
                            labels: Array.from({ length: 7 }, (_, i) => i),
                            datasets: [
                              {
                                data: buildRecentRevenueSeries(
                                  overallInvoices,
                                  7
                                ),
                                borderColor: "#0d6efd",
                                backgroundColor: "rgba(13,110,253,0.08)",
                                fill: true,
                              },
                            ],
                          }}
                          options={sparkOptions}
                        />
                      </div>

                      <div className="mt-2 d-flex justify-content-between align-items-center">
                        <small className="text-muted">7-day revenue</small>
                        <small className="text-success fw-semibold">
                          {buildRecentRevenueSeries(overallInvoices, 7)
                            .reduce((s, v) => s + v, 0)
                            .toLocaleString()}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Total Revenue */}
                <div className="col-md-3">
                  <div className="card h-100 border-success shadow-sm dashboard-card">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <small className="text-muted">Total Revenue</small>
                          <h3 className="mb-0">
                            {overallRevenue.toLocaleString()}
                          </h3>
                          <small className="text-muted">
                            sum of invoice totals
                          </small>
                        </div>
                        <div>
                          <i
                            className="bi bi-currency-dollar fs-3 text-success"
                            aria-hidden
                          />
                        </div>
                      </div>

                      <div style={{ height: 40 }}>
                        <Line
                          data={{
                            labels: Array.from({ length: 7 }, (_, i) => i),
                            datasets: [
                              {
                                data: buildRecentRevenueSeries(
                                  overallInvoices,
                                  7
                                ),
                                borderColor: "#198754",
                                backgroundColor: "rgba(25,135,84,0.08)",
                                fill: true,
                              },
                            ],
                          }}
                          options={sparkOptions}
                        />
                      </div>

                      <div className="mt-3">
                        <small className="text-muted">Month vs Prev</small>
                        {/* a simple percent placeholder — replace with real calculation if you want */}
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-success">+7.4%</span>
                          <small className="text-muted">
                            vs previous period
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 3: Paid */}
                <div className="col-md-3">
                  <div className="card h-100 border-success shadow-sm dashboard-card">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <small className="text-muted">Paid</small>
                          <h3 className="mb-0">
                            {statusSummary.paid?.count ?? 0}
                          </h3>
                          <small className="text-muted">paid invoices </small>
                        </div>
                        <div>
                          <i
                            className="bi bi-check-circle fs-3 text-success"
                            aria-hidden
                          />
                        </div>
                      </div>

                      {/* progress showing percent paid */}
                      <div className="mt-auto">
                        {(() => {
                          const paid = statusSummary.paid?.count ?? 0;
                          const total = overallInvoices.length || 1;
                          const pct = Math.round((paid / total) * 100);
                          return (
                            <>
                              <div className="d-flex justify-content-between">
                                <small className="text-muted">Paid %</small>
                                <small className="fw-semibold">{pct}%</small>
                              </div>
                              <div className="progress" style={{ height: 8 }}>
                                <div
                                  className="progress-bar bg-success"
                                  role="progressbar"
                                  style={{ width: `${pct}%` }}
                                  aria-valuenow={pct}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                ></div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 4: Pending / Partial */}
                <div className="col-md-3">
                  <div className="card h-100 border-warning shadow-sm dashboard-card">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <small className="text-muted">
                            Pending / Partial
                          </small>
                          <h3 className="mb-0">
                            {(statusSummary.notpaid?.count ?? 0) +
                              (statusSummary.partialpaid?.count ??
                                statusSummary.partial?.count ??
                                0)}
                          </h3>
                          <small className="text-muted">
                            pending invoices{" "}
                          </small>
                        </div>
                        <div>
                          <i
                            className="bi bi-clock-history fs-3 text-warning"
                            aria-hidden
                          />
                        </div>
                      </div>

                      <div style={{ height: 40 }}>
                        <Line
                          data={{
                            labels: Array.from({ length: 7 }, (_, i) => i),
                            datasets: [
                              {
                                data: buildRecentRevenueSeries(
                                  overallInvoices,
                                  7
                                ).map((v) => v * 0.25),
                                borderColor: "#ffc107",
                                backgroundColor: "rgba(255,193,7,0.06)",
                                fill: true,
                              },
                            ],
                          }}
                          options={sparkOptions}
                        />
                      </div>

                      <div className="mt-3 d-flex justify-content-between">
                        <small className="text-muted">Avg pending amount</small>
                        <small className="fw-semibold">
                          {(
                            (buildRecentRevenueSeries(
                              overallInvoices,
                              7
                            ).reduce((s, v) => s + v, 0) /
                              7) *
                            0.25
                          ) // placeholder
                            .toLocaleString()}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status + Timeseries row */}
              <div className="row mb-4">
                {/* STATUS SECTION */}
                <div className="col-lg-6 mb-3">
                  <div className="card h-100 shadow-sm border-0">
                    <div className="card-header d-flex justify-content-between align-items-start bg-light">
                      {/* LEFT — allow wrapping */}
                      <div style={{ minWidth: 0 }}>
                        <strong
                          style={{
                            display: "inline-block",
                            lineHeight: 1.3,
                            whiteSpace: "normal",
                            cursor: "help",
                          }}
                        >
                          <i className="bi bi-graph-up-arrow text-primary me-2"></i>
                          Invoice Status Distribution
                          <Tip text="Shows how invoices are distributed among Paid, Not Paid, and Partial statuses.">
                            <i
                              className="fa fa-info-circle text-primary ms-2"
                              style={{ cursor: "pointer", fontSize: 14 }}
                            />
                          </Tip>
                        </strong>

                        <small className="text-muted d-block">
                          Breakdown of paid & pending invoices
                          {statusFilter
                            ? ` (Filtered: ${statusFilter.toUpperCase()})`
                            : ""}
                        </small>
                      </div>

                      {/* RIGHT — NEVER WRAPS */}
                      <div
                        style={{
                          flexShrink: 0, // 🔑 key line
                          marginLeft: 12,
                        }}
                      >
                        <select
                          className="form-select form-select-sm"
                          value={sectionPeriods["status"] ?? ""}
                          onChange={(e) =>
                            setSectionPeriod(
                              "status",
                              (e.target.value as PeriodKey) || ""
                            )
                          }
                        >
                          <option value="">Use Global Filter</option>
                          <option value="overall">Overall (All Time)</option>
                          {PERIODS.filter((p) => p.key !== "overall").map(
                            (p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="card-body">
                      {invoices.length === 0 || Object.values(statusSummary).every((x: any) => x.count === 0) ? (
                        <div className="text-center py-5">
                          <i className="bi bi-receipt text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for invoice insights
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* DONUT */}
                          <div className="d-flex align-items-center justify-content-center mb-3">
                            <div style={{ width: "200px", height: "200px" }}>
                              <Doughnut
                                data={{
                                  ...statusPieData,
                                  datasets: [
                                    {
                                      ...(statusPieData.datasets[0] as any),
                                      borderWidth: 2,
                                      borderColor: "#fff",
                                      hoverOffset: 6,
                                      cutout: "55%",
                                    } as any,
                                  ],
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                      callbacks: {
                                        label: (ctx) => {
                                          let label = ctx.label || "";
                                          let value = ctx.parsed || 0;
                                          let total = Object.values(
                                            statusSummary
                                          ).reduce(
                                            (s: number, x: any) => s + x.count,
                                            0
                                          );
                                          let pct = total
                                            ? ((value / total) * 100).toFixed(1)
                                            : "0";
                                          return `${label}: ${value} (${pct}%)`;
                                        },
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                          </div>

                      {/* TOTAL */}
                      <div className="text-center mb-2">
                        <small className="text-muted">
                          Total invoices:{" "}
                          <strong>
                            {Object.values(statusSummary).reduce(
                              (s: number, x: any) => s + x.count,
                              0
                            )}
                          </strong>
                        </small>
                      </div>

                      {/* CLICKABLE BADGES */}
                      <div className="d-flex flex-wrap gap-2 justify-content-center">
                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            statusFilter === "paid"
                              ? "border border-success"
                              : ""
                          }`}
                          style={{
                            background: "rgba(25,135,84,0.15)",
                            color: "#198754",
                            cursor: "pointer",
                          }}
                          onClick={() => toggleStatus("paid")}
                        >
                          ● Paid:{" "}
                          <strong>{statusSummary.paid?.count ?? 0}</strong>
                        </span>

                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            statusFilter === "notpaid"
                              ? "border border-danger"
                              : ""
                          }`}
                          style={{
                            background: "rgba(220,53,69,0.15)",
                            color: "#dc3545",
                            cursor: "pointer",
                          }}
                          onClick={() => toggleStatus("notpaid")}
                        >
                          ● Not Paid:{" "}
                          <strong>{statusSummary.notpaid?.count ?? 0}</strong>
                        </span>

                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            statusFilter === "partial"
                              ? "border border-warning"
                              : ""
                          }`}
                          style={{
                            background: "rgba(255,193,7,0.15)",
                            color: "#ffc107",
                            cursor: "pointer",
                          }}
                          onClick={() => toggleStatus("partial")}
                        >
                          ● Partial:{" "}
                          <strong>
                            {statusSummary.partial?.count ??
                              statusSummary.partialpaid?.count ??
                              0}
                          </strong>
                        </span>
                      </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 mb-3">
                  <div className="card h-100 shadow-sm border-0">
                    {/* HEADER */}
                    <div className="card-header d-flex justify-content-between align-items-start bg-light">
                      <div style={{ minWidth: 0 }}>
                        <strong
                          style={{
                            display: "inline-block",
                            lineHeight: 1.3,
                            whiteSpace: "normal",
                            cursor: "help",
                          }}
                        >
                          <i className="bi bi-activity text-primary me-2"></i>
                          Revenue Over Time
                          <Tip text="Visualizes daily or monthly revenue trends based on selected timeframe.">
                            <i
                              className="fa fa-info-circle ms-2 text-primary"
                              style={{ cursor: "pointer", fontSize: 14 }}
                            />
                          </Tip>
                        </strong>
                        <small className="text-muted d-block">
                          {timeseries.groupByMonth
                            ? "Monthly trend"
                            : "Daily trend"}{" "}
                          of invoice revenue
                        </small>
                      </div>
                      <div
                        style={{
                          flexShrink: 0, // 🔑 key line
                          marginLeft: 12,
                        }}
                      >
                        <select
                          className="form-select form-select-sm"
                          value={sectionPeriods["timeseries"] ?? ""}
                          onChange={(e) =>
                            setSectionPeriod(
                              "timeseries",
                              (e.target.value as PeriodKey) || ""
                            )
                          }
                        >
                          <option value="">Use Global Filter</option>
                          <option value="overall">Overall (All Time)</option>

                          {PERIODS.filter((p) => p.key !== "overall").map(
                            (p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>

                    {/* BODY */}
                    <div className="card-body py-3">
                      {/* MINI SUMMARY ROW */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <small className="text-muted">Total Revenue</small>
                          <div
                            className="fw-semibold"
                            style={{ color: "#0d6efd" }}
                          >
                            {timeseries.data
                              .reduce((s, v) => s + v, 0)
                              .toLocaleString()}
                          </div>
                        </div>

                        <div className="text-end">
                          <small className="text-muted">Average</small>
                          <div
                            className="fw-semibold"
                            style={{ color: "#20c997" }}
                          >
                            {Math.round(
                              timeseries.data.reduce((s, v) => s + v, 0) /
                                (timeseries.data.length || 1)
                            ).toLocaleString()}
                          </div>
                        </div>

                        <div className="text-end">
                          <small className="text-muted">Peak</small>
                          <div className="fw-semibold text-danger">
                            {Math.max(...timeseries.data).toLocaleString()}
                          </div>
                        </div>
                      </div>

                      {/* LINE CHART */}
                      {invoices.length === 0 || timeseries.data.length === 0 || timeseries.data.every(v => v === 0) ? (
                        <div className="text-center py-5">
                          <i className="bi bi-receipt text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for invoice insights
                          </p>
                        </div>
                      ) : (
                        <div
                          style={{
                            height: 260,
                            position: "relative",
                          }}
                        >
                          <Line
                            data={timeseriesData as any}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              elements: {
                                line: { borderWidth: 3 },
                                point: { radius: 2, hoverRadius: 5 },
                              },
                              scales: {
                                x: {
                                  type: "time",
                                  grid: { display: false },
                                  time: {
                                    unit: timeseries.groupByMonth
                                      ? "month"
                                      : "day",
                                    tooltipFormat: timeseries.groupByMonth
                                      ? "MMM YYYY"
                                      : "DD MMM",
                                  },
                                  ticks: {
                                    maxRotation: 0,
                                    autoSkipPadding: 8,
                                  },
                                },
                                y: {
                                  beginAtZero: true,
                                  grid: { color: "rgba(0,0,0,0.05)" },
                                  ticks: {
                                  callback: (value) => value.toLocaleString(),
                                },
                              },
                            },
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                callbacks: {
                                  label: (ctx) =>
                                    `₹ ${ctx?.parsed?.y?.toLocaleString()} revenue`,
                                },
                              },
                            },
                          }}
                        />
                        </div>
                      )}
                    </div>

                    {/* FOOTER SPARKLINE */}
                    <div className="card-footer bg-white border-0 pt-0">
                      <small className="text-muted">
                        Last 7-day micro trend
                      </small>

                      <div style={{ height: 45 }}>
                        <Line
                          data={{
                            labels: Array.from({ length: 7 }, (_, i) => i),
                            datasets: [
                              {
                                data: buildRecentRevenueSeries(
                                  overallInvoices,
                                  7
                                ),
                                borderColor: "#0d6efd",
                                backgroundColor: "rgba(13,110,253,0.10)",
                                fill: true,
                                tension: 0.3,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            elements: {
                              point: { radius: 0 },
                              line: { borderWidth: 1.8 },
                            },
                            plugins: {
                              legend: { display: false },
                              tooltip: { enabled: false },
                            },
                            scales: {
                              x: { display: false },
                              y: { display: false },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Owners + Products row */}
              <div className="row mb-4">
                <div className="col-lg-7 mb-3">
                  <div className="card h-100 shadow-sm border-0">
                    {/* HEADER */}
                    <div className="card-header d-flex justify-content-between align-items-start bg-light">
                      <div style={{ minWidth: 0 }}>
                        <strong
                          style={{
                            display: "inline-block",
                            lineHeight: 1.3,
                            whiteSpace: "normal",
                            cursor: "help",
                          }}
                        >
                          <i className="bi bi-people text-primary me-2"></i>Lead
                          Owner Activity
                          <Tip text="Shows how each owner is performing based on Paid, Not Paid, and Partial invoices.">
                            <i
                              className="fa fa-info-circle ms-2 text-primary"
                              style={{ cursor: "pointer", fontSize: 14 }}
                            />
                          </Tip>
                        </strong>
                        <small className="text-muted d-block">
                          Paid vs Pending breakdown per owner
                        </small>
                      </div>

                      <div
                        style={{
                          flexShrink: 0,
                          marginLeft: 12,
                        }}
                      >
                        <select
                          className="form-select form-select-sm"
                          value={sectionPeriods["owner"] ?? ""}
                          onChange={(e) =>
                            setSectionPeriod(
                              "owner",
                              (e.target.value as PeriodKey) || ""
                            )
                          }
                        >
                          <option value="">Use Global Filter</option>
                          <option value="overall">Overall (All Time)</option>

                          {PERIODS.filter((p) => p.key !== "overall").map(
                            (p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>

                    {/* BODY */}
                    <div className="card-body" style={{ minHeight: 300 }}>
                      {invoices.length === 0 || Object.keys(ownerActivity).length === 0 ? (
                        <div className="text-center py-5">
                          <i className="bi bi-receipt text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for invoice insights
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* FIXED HEIGHT BAR WRAPPER */}
                          <div style={{ height: 280, overflow: "hidden" }}>
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                overflowX: "auto",
                              }}
                            >
                              <Bar
                                ref={ownerBarRef}
                                data={ownerBarData as any}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                legend: { position: "bottom" },
                                tooltip: {
                                  callbacks: {
                                    label: (ctx) =>
                                      `${
                                        ctx.dataset.label
                                      }: ${ctx.parsed.y?.toLocaleString()}`,
                                  },
                                },
                              },
                              scales: {
                                x: {
                                  stacked: true,
                                  ticks: {
                                    autoSkip: false,
                                    maxRotation: 45,
                                    minRotation: 0,
                                  },
                                },
                                y: { stacked: true, beginAtZero: true },
                              },
                            }}
                            onClick={onOwnerBarClick}
                          />
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-3 text-muted small">
                        {Object.keys(ownerActivity).length === 0 && (
                          <div>No owner data in this selection</div>
                        )}

                        {ownerFilter && (
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-info text-dark px-3 py-2">
                              <i className="bi bi-person-circle me-1"></i>
                              {ownerFilter}
                            </span>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setOwnerFilter(null)}
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-5 mb-3">
                  <div className="card h-100 shadow-sm border-0">
                    {/* HEADER */}
                    <div className="card-header d-flex justify-content-between align-items-start bg-light">
                      <div style={{ minWidth: 0 }}>
                        <strong
                          style={{
                            display: "inline-block",
                            lineHeight: 1.3,
                            whiteSpace: "normal",
                            cursor: "help",
                          }}
                        >
                          <i className="bi bi-box-seam text-warning me-2"></i>
                          Product Wise Revenue
                          <Tip text="Shows revenue contribution from each product and paid/unpaid distribution.">
                            <i
                              className="fa fa-info-circle ms-2 text-primary me-2"
                              style={{ cursor: "pointer", fontSize: 14 }}
                            />
                          </Tip>
                        </strong>
                        <small className="text-muted d-block">
                          Contribution of each product
                        </small>
                      </div>

                      {/* Filter Dropdown */}
                      <div
                        style={{
                          flexShrink: 0, // 🔑 key line
                          marginLeft: 12,
                        }}
                      >
                        <select
                          className="form-select form-select-sm"
                          value={sectionPeriods["product"] ?? ""}
                          onChange={(e) =>
                            setSectionPeriod(
                              "product",
                              (e.target.value as PeriodKey) || ""
                            )
                          }
                        >
                          <option value="">Use Global Filter</option>
                          <option value="overall">Overall (All Time)</option>

                          {PERIODS.filter((p) => p.key !== "overall").map(
                            (p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>

                    {/* BODY */}
                    <div className="card-body d-flex flex-column align-items-center">
                      {invoices.length === 0 || Object.keys(productActivity).length === 0 ? (
                        <div className="text-center py-5">
                          <i className="bi bi-receipt text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for invoice insights
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* DONUT (fixed height) */}
                          <div style={{ width: "200px", height: "200px" }}>
                            <Doughnut
                              data={{
                                ...productDoughnutData,
                                datasets: [
                                  {
                                    ...(productDoughnutData.datasets[0] as any),
                                    borderWidth: 2,
                                    borderColor: "#fff",
                                    hoverOffset: 8,
                                    cutout: "55%",
                                  } as any,
                                ],
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                              }}
                            />
                          </div>

                      {/* LEGEND SUMMARY */}
                      <div className="mt-2 text-center">
                        <small className="text-muted">
                          Total revenue:{" "}
                          <strong>
                            {Object.values(productActivity)
                              .reduce((s, x) => s + x.revenue, 0)
                              .toLocaleString()}
                          </strong>
                        </small>
                      </div>

                      {/* PRODUCT LIST WITH MINI-BAR */}
                      <div
                        className="mt-3 w-100"
                        style={{ maxHeight: 200, overflowY: "auto" }}
                      >
                        <ul className="list-unstyled mb-0">
                          {Object.entries(productActivity)
                            .sort((a, b) => b[1].revenue - a[1].revenue)
                            .map(([product, meta]) => {
                              const maxRevenue = Math.max(
                                ...Object.values(productActivity).map(
                                  (p) => p.revenue
                                )
                              );
                              const barWidth = maxRevenue
                                ? (meta.revenue / maxRevenue) * 100
                                : 0;

                              return (
                                <li
                                  key={product}
                                  className="py-2 border-bottom d-flex justify-content-between align-items-center"
                                >
                                  <div>
                                    <div className="fw-semibold">{product}</div>
                                    <small className="text-muted">
                                      {meta.count} invoices
                                    </small>
                                  </div>

                                  <div
                                    className="text-end"
                                    style={{ minWidth: 120 }}
                                  >
                                    <div className="fw-semibold">
                                      {meta.revenue.toLocaleString()}
                                    </div>
                                    <small className="text-muted">
                                      Paid: {meta.paid} / Unpaid: {meta.notpaid}
                                    </small>

                                    {/* MINI BAR */}
                                    <div
                                      className="mt-1"
                                      style={{
                                        height: 6,
                                        background: "#e9ecef",
                                        borderRadius: 10,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${barWidth}%`,
                                          background: "#ffc107",
                                          height: "100%",
                                        }}
                                      ></div>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Upcoming dates */}
              <div className="row">
                {/* UPCOMING VALIDATION */}
                <div className="col-md-6 mb-3">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-header bg-light d-flex align-items-center gap-2">
                      <strong>
                        {" "}
                        <i className="bi bi-calendar-week text-primary me-2"></i>
                        Upcoming Validation Dates (next 30 days)
                        <Tip text="Invoices with validation deadlines happening in the next 30 days.">
                          <i
                            className="fa fa-info-circle ms-2 text-primary"
                            style={{ cursor: "pointer", fontSize: 14 }}
                          />
                        </Tip>
                      </strong>
                    </div>

                    <div
                      className="card-body"
                      style={{ maxHeight: 300, overflowY: "auto" }}
                    >
                      {upcomingValidation.length === 0 ? (
                        <div className="text-muted text-center py-4">
                          No Upcoming Validations
                        </div>
                      ) : (
                        upcomingValidation.map((inv) => (
                          <div
                            key={inv.invoice_proposal_id}
                            className="d-flex justify-content-between align-items-center py-3 border-bottom"
                          >
                            <div>
                              <div className="fw-semibold">
                                {inv.deal?.contact?.company_name ??
                                  "Unknown Company"}
                              </div>
                              <small className="text-muted">
                                {inv.deal?.contact?.contact_person_name ?? "—"}
                              </small>
                            </div>

                            <div className="text-end">
                              <span className="badge text-bg-primary mb-1">
                                {moment(inv.validation_date).format(
                                  "DD MMM YYYY"
                                )}
                              </span>
                              <div className="fw-semibold text-success">
                                {(
                                  inv.invoice_total_amount ?? 0
                                ).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* UPCOMING INVOICE DATES */}
                <div className="col-md-6 mb-3">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-header bg-light d-flex align-items-center gap-2">
                      <strong>
                        <i className="bi bi-receipt text-warning me-2"></i>
                        Upcoming Invoice Dates (next 30 days)
                        <Tip
                          text="Proposals scheduled to be generated or sent within the upcoming 30 days."
                          placement="top"
                        >
                          <i
                            className="fa fa-info-circle text-primary ms-2"
                            style={{ cursor: "pointer", fontSize: 14 }}
                          />
                        </Tip>
                      </strong>
                    </div>

                    <div
                      className="card-body"
                      style={{ maxHeight: 300, overflowY: "auto" }}
                    >
                      {upcomingInvoiceDates.length === 0 ? (
                        <div className="text-muted text-center py-4">
                          No Upcoming Invoices
                        </div>
                      ) : (
                        upcomingInvoiceDates.map((inv) => (
                          <div
                            key={inv.invoice_proposal_id}
                            className="d-flex justify-content-between align-items-center py-3 border-bottom"
                          >
                            <div>
                              <div className="fw-semibold">
                                {inv.deal?.contact?.company_name ??
                                  "Unknown Company"}
                              </div>
                              <small className="text-muted">
                                {inv.deal?.contact?.contact_person_name ?? "—"}
                              </small>
                            </div>

                            <div className="text-end">
                              <span className="badge text-bg-warning mb-1">
                                {moment(inv.invoice_date).format("DD MMM YYYY")}
                              </span>
                              <div className="fw-semibold text-primary">
                                {(
                                  inv.invoice_total_amount ?? 0
                                ).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
}
